import {
  QsgV1Model,
  SectionModel,
  ContentFeatureModel,
  DisclaimerFeatureModel,
  MediaFeatureModel,
  IconlistFeatureModel,
  FeatureModelUnion,
} from '../../fetch-data/graphql/types';
import {
  isContentFeature,
  isDisclaimerFeature,
  isMediaFeature,
  isIconListFeature,
} from './utils';
import stopwordsData from './lexical/stopwords.json';
import synonymsData from './lexical/synonyms.json';
import exceptionsData from './lexical/exceptions.json';
import striptags from 'striptags';

interface AutocompleteOption {
  label: string;
  value: string;
  type?: 'guide' | 'facet' | 'fallback';
  score?: number;
}

interface IndexedGuide {
  id: string;
  title: string;
  sectionTitle: string;
  sectionId: string;
  primaryTags: string[];
  secondaryTags: string[];
  features: string[];
  models: string[];
  description: string;
  contentItems: Array<{ markdown?: string | null; plaintext?: string | null }>;
  sectionPath: string[];
}

const EXCEPTIONS = new Set(exceptionsData);

const COMMON_ABBREVIATIONS: Record<string, string> = {
  bt: 'bluetooth',
  ota: 'over-the-air',
  ac: 'climate',
  map: 'navigation',
  nav: 'navigation',
};

const STOPWORDS = new Set(stopwordsData);

function buildSynonymsMap(): Record<string, string> {
  const synonymsMap: Record<string, string> = {};
  const synonyms = synonymsData as Record<string, string[]>;
  

  for (const [key, values] of Object.entries(synonyms)) {
    const normalizedKey = key.toLowerCase();
    synonymsMap[normalizedKey] = normalizedKey;

    for (const value of values) {
      const normalizedValue = value.toLowerCase();
      synonymsMap[normalizedValue] = normalizedKey;
    }
  }

  for (const [abbr, full] of Object.entries(COMMON_ABBREVIATIONS)) {
    if (!synonymsMap[abbr]) {
      synonymsMap[abbr] = full;
    }
  }

  return synonymsMap;
}

const SYNONYMS = buildSynonymsMap();

const VW_MODELS = [
  'id.3',
  'id.4',
  'id.7',
  'golf',
  'gti',
  'tiguan',
  'atlas',
  'taos',
  'jetta',
  'passat',
  'arteon',
  'polo',
  'e-golf',
];

const SCORING_FIELDS: Array<keyof IndexedGuide> = [
  'title',
  'primaryTags',
  'secondaryTags',
  'features',
  'models',
  'description',
];

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function tokenize(query: string): string[] {
  return normalize(query)
    .split(/[\s]+/)
    .filter(Boolean);
}

function isExceptionToken(token: string): boolean {
  if (EXCEPTIONS.has(token)) return true;
  const hyphenated = token.replace(/[\s]+/g, '-');
  return EXCEPTIONS.has(hyphenated);
}

function stem(token: string): string {
  if (token.endsWith('ing') && token.length > 4) return token.slice(0, -3);
  if (token.endsWith('ed') && token.length > 3) return token.slice(0, -2);
  if (token.endsWith('s') && token.length > 3) return token.slice(0, -1);
  return token;
}

function applySynonyms(tokens: string[]): string[] {
  return tokens.map((t) => SYNONYMS[t] || t);
}

function preprocess(query: string): string[] {
  let tokens = tokenize(query);
  tokens = applySynonyms(tokens);
  const contentTokens: string[] = [];
  for (const t of tokens) {
    if (isExceptionToken(t)) {
      contentTokens.push(t);
    } else if (!STOPWORDS.has(t)) {
      contentTokens.push(stem(t));
    }
  }
  return contentTokens;
}

function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // deletion
        dp[i][j - 1] + 1, // insertion
        dp[i - 1][j - 1] + cost // substitution
      );
    }
  }
  return dp[m][n];
}

function fieldWeight(fieldName: string): number {
  switch (fieldName) {
    case 'title':
      return 1.0;
    case 'primaryTags':
      return 0.7;
    case 'secondaryTags':
      return 0.5;
    case 'features':
      return 0.7;
    case 'models':
      return 0.8;
    case 'description':
      return 0.3;
    default:
      return 0;
  }
}

function tokenizeFieldValue(value: string | string[]): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((v) => normalize(v).split(/\s+/));
  }
  return normalize(value).split(/\s+/);
}

function textContainsQuery(text: string, contentTokens: string[], originalQuery: string): boolean {
  if (!text || text.trim() === '') return false;
  const normalizedText = normalize(text);
  const normalizedQuery = normalize(originalQuery);

  // Check exact phrase
  if (originalQuery.length >= 3 && normalizedText.includes(normalizedQuery)) {
    return true;
  }

  // Check if any token appears
  for (const t of contentTokens) {
    if (!t || t.trim() === '') continue;
    if (normalizedText.includes(t)) {
      return true;
    }
    // Check prefix match on words
    const words = tokenizeFieldValue(text);
    if (words.some((w) => w.startsWith(t))) {
      return true;
    }
  }

  return false;
}

function extractMarkdownTitles(markdown: string | null | undefined): string[] {
  if (!markdown) return [];
  const titles: string[] = [];
  const lines = markdown.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const headerMatch = trimmed.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      const titleText = headerMatch[1]
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
        .trim();

      if (titleText) {
        titles.push(titleText);
      }
    }
  }

  return titles;
}

function scoreGuide(
  guide: IndexedGuide,
  contentTokens: string[],
  originalQuery: string
): { score: number; titleMatched: boolean; contentMatched: boolean; contentTitles: string[] } {
  let score = 0;
  let titleMatched = false;
  let contentMatched = false;
  const contentTitles: string[] = [];

  // Check if title matches
  titleMatched = textContainsQuery(guide.title, contentTokens, originalQuery);

  // Check if description/content matches
  contentMatched = textContainsQuery(guide.description, contentTokens, originalQuery);

  // Extract markdown titles from content if content matches
  if (contentMatched) {
    for (const contentItem of guide.contentItems) {
      if (contentItem.markdown) {
        const titles = extractMarkdownTitles(contentItem.markdown);
        for (const title of titles) {
          // Only include markdown titles that contain the query
          if (textContainsQuery(title, contentTokens, originalQuery)) {
            contentTitles.push(title);
          }
        }
      }
    }
  }

  // Only score if there's an actual match
  if (!titleMatched && !contentMatched) {
    return { score: 0, titleMatched: false, contentMatched: false, contentTitles: [] };
  }

  for (const t of contentTokens) {
    for (const field of SCORING_FIELDS) {
      const weight = fieldWeight(field);
      const values = Array.isArray(guide[field])
        ? (guide[field] as string[])
        : [guide[field] as string];

      for (const v of values) {
        if (!v || v.trim() === '') continue;
        
        const val = normalize(v);
        const normalizedQuery = normalize(originalQuery);

        // Exact phrase match (if query length >= 3)
        if (originalQuery.length >= 3 && val.includes(normalizedQuery)) {
          score += 3.0 + weight;
          continue;
        }

        // Prefix match on any word in field
        const words = tokenizeFieldValue(v);
        if (words.some((w) => w.startsWith(t))) {
          score += 2.0 + weight;
          continue;
        }

        // Substring match
        if (val.includes(t)) {
          score += 1.0 + weight;
          continue;
        }

        // Fuzzy match (avoid very short tokens)
        if (t.length >= 3) {
          const minDist = Math.min(...words.map((w) => editDistance(t, w)));
          if (minDist === 1) {
            score += 0.5 + weight * 0.5;
          } else if (t.length >= 6 && minDist === 2) {
            score += 0.2 + weight * 0.3;
          }
        }
      }
    }

    // Domain lexicon boost
    if (isExceptionToken(t)) {
      score += 2.0;
    }
  }

  return { score, titleMatched, contentMatched, contentTitles };
}

function extractTextFromFeature(feature: FeatureModelUnion): string[] {
  const texts: string[] = [];

  if (isContentFeature(feature)) {
    const contentFeature = feature as ContentFeatureModel;
    if (contentFeature.title?.plaintext) {
      texts.push(contentFeature.title.plaintext);
    }
    if (contentFeature.content) {
      for (const content of contentFeature.content) {
        if (content.plaintext) {
          texts.push(content.plaintext);
        }
      }
    }
  } else if (isDisclaimerFeature(feature)) {
    const disclaimerFeature = feature as DisclaimerFeatureModel;
    if (disclaimerFeature.disclaimerContent?.plaintext) {
      texts.push(disclaimerFeature.disclaimerContent.plaintext);
    }
  } else if (isMediaFeature(feature)) {
    const mediaFeature = feature as MediaFeatureModel;
    if (mediaFeature.caption?.plaintext) {
      texts.push(mediaFeature.caption.plaintext);
    }
  } else if (isIconListFeature(feature)) {
    const iconListFeature = feature as IconlistFeatureModel;
    if (iconListFeature.items) {
      for (const item of iconListFeature.items) {
        if (item.html) {
          texts.push(striptags(item.html));
        } else if (item.plaintext) {
          texts.push(item.plaintext);
        }
      }
    }
  }

  return texts;
}

function buildIndex(
  sections: SectionModel[] | null | undefined,
  sectionPath: string[] = []
): IndexedGuide[] {
  const indexed: IndexedGuide[] = [];

  if (!sections) return indexed;

  for (const section of sections) {
    if (!section) continue;

    const currentPath = [...sectionPath, section.sectionId || ''];
    const sectionTitle = section.sectionTitle?.plaintext || '';

    // Extract keywords from section
    const sectionKeywords: string[] = [];
    if (section.sectionKeywords) {
      for (const keyword of section.sectionKeywords) {
        if (keyword.plaintext) {
          sectionKeywords.push(keyword.plaintext);
        }
      }
    }

    // Process features
    if (section.features) {
      for (const feature of section.features) {
        if (!feature) continue;

        const featureTexts = extractTextFromFeature(feature);
        const allText = featureTexts.join(' ');

        // Extract title from content feature
        let title = sectionTitle;
        if (isContentFeature(feature)) {
          const contentFeature = feature as ContentFeatureModel;
          if (contentFeature.title?.plaintext) {
            title = contentFeature.title.plaintext;
          }
        }

        // Extract tags/features/models from text (simplified - you may want to enhance this)
        const primaryTags: string[] = [...sectionKeywords];
        const secondaryTags: string[] = [];
        const features: string[] = [];
        const models: string[] = [];

        // Try to identify models and features from text
        const normalizedText = normalize(allText);
        for (const exception of EXCEPTIONS) {
          const normalizedException = normalize(exception);
          if (normalizedText.includes(normalizedException)) {
            if (VW_MODELS.includes(exception)) {
              models.push(exception);
            } else {
              features.push(exception);
            }
          }
        }

        // Use ALL text content for description - this includes all content items
        const description = allText || '';

        // Store content items for markdown title extraction
        const contentItems: Array<{ markdown?: string | null; plaintext?: string | null }> = [];
        if (isContentFeature(feature)) {
          const contentFeature = feature as ContentFeatureModel;
          if (contentFeature.content) {
            for (const contentItem of contentFeature.content) {
              contentItems.push({
                markdown: contentItem.markdown,
                plaintext: contentItem.plaintext,
              });
            }
          }
        }

        indexed.push({
          id: `${section.sectionId}-${feature._id || ''}`,
          title,
          sectionTitle,
          sectionId: section.sectionId || '',
          primaryTags,
          secondaryTags,
          features,
          models,
          description,
          contentItems,
          sectionPath: currentPath,
        });
      }
    }

    // Process subsections recursively
    if (section.subsections) {
      indexed.push(...buildIndex(section.subsections, currentPath));
    }
  }

  return indexed;
}

function generateSuggestions(
  query: string,
  index: IndexedGuide[],
  limit: number = 7
): AutocompleteOption[] {
  const q = query.trim();

  if (q.length < 3) {
    return [];
  }

  /*if (q.length < 2) {
    // For very short queries, only show strong domain prefixes
    if (q.length === 0) return [];
    const shortResults: AutocompleteOption[] = [];
    for (const exception of EXCEPTIONS) {
      if (exception && exception.trim() !== '' && normalize(exception).startsWith(normalize(q))) {
        shortResults.push({
          label: exception,
          value: exception,
          type: 'facet',
        });
        if (shortResults.length >= 3) break;
      }
    }
    // Filter out options with empty labels or values
    return shortResults.filter(
      (option) => option.label && option.label.trim() !== '' && option.value && option.value.trim() !== ''
    );
  }*/

  const contentTokens = preprocess(q);
  if (contentTokens.length === 0) {
    // Only stopwords - delay suggestions
    return [{ type: 'fallback', label: `Search for "${q}"`, value: q }];
  }

  // Score each guide
  const scored = index
    .map((g) => {
      const result = scoreGuide(g, contentTokens, q);
      return {
        guide: g,
        score: result.score,
        titleMatched: result.titleMatched,
        contentMatched: result.contentMatched,
        contentTitles: result.contentTitles,
      };
    })
    .filter((item) => item.score > 0.5 && (item.titleMatched || item.contentMatched)); // Must match in title OR content

  // Rank and de-duplicate
  scored.sort((a, b) => b.score - a.score);

  // Limit fuzzy-only results to 2
  const topGuides: AutocompleteOption[] = [];
  let fuzzyCount = 0;
  const seenLabels = new Set<string>();

  for (const item of scored) {
    if (topGuides.length >= limit - 1) break; // Reserve space for fallback

    // If title matches, show the title
    if (item.titleMatched) {
      const title = item.guide.title;
      if (title && title.trim() !== '' && !seenLabels.has(title.toLowerCase())) {
        seenLabels.add(title.toLowerCase());
        
        // Check if this is a fuzzy-only match
        const isFuzzyOnly = item.score < 2.0;
        if (isFuzzyOnly && fuzzyCount >= 2) continue;
        if (isFuzzyOnly) fuzzyCount++;

        topGuides.push({
          type: 'guide',
          label: title,
          value: title,
          score: item.score,
        });
      }
    }

    // If content matches, show markdown titles from content
    if (item.contentMatched && item.contentTitles.length > 0) {
      for (const contentTitle of item.contentTitles) {
        if (topGuides.length >= limit - 1) break;
        
        if (contentTitle && contentTitle.trim() !== '' && !seenLabels.has(contentTitle.toLowerCase())) {
          seenLabels.add(contentTitle.toLowerCase());
          
          // Check if this is a fuzzy-only match
          const isFuzzyOnly = item.score < 2.0;
          if (isFuzzyOnly && fuzzyCount >= 2) continue;
          if (isFuzzyOnly) fuzzyCount++;

          topGuides.push({
            type: 'guide',
            label: contentTitle,
            value: contentTitle,
            score: item.score * 0.8, // Slightly lower score for content titles
          });
        }
      }
    }
  }

  // Sort results: prioritize exact word matches, then by score
  const queryWord = q.toLowerCase();
  
  topGuides.sort((a, b) => {
    const aLabel = a.label?.toLowerCase() || '';
    const bLabel = b.label?.toLowerCase() || '';
    
    // Check for exact word match (word boundary)
    const aExact = new RegExp(`\\b${queryWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(aLabel);
    const bExact = new RegExp(`\\b${queryWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(bLabel);
    
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;
    
    return (b.score || 0) - (a.score || 0);
  });

  const results = [...topGuides];

  // Fallback
  results.push({
    type: 'fallback',
    label: `Search for "${q}"`,
    value: q,
  });

  // Filter out options with empty labels or values
  return results
    .filter((option) => option.label && option.label.trim() !== '' && option.value && option.value.trim() !== '')
    .slice(0, limit);
}

/**
 * Gets autocomplete options for a search term based on guide content
 *
 * @param guide - The QSG guide data
 * @param searchTerm - The current search term
 * @param limit - Maximum number of options to return (default: 7)
 * @returns Array of autocomplete options with label and value
 */
export const getAutocompleteOptions = (
  guide: QsgV1Model | null | undefined,
  searchTerm: string,
  limit: number = 30
): AutocompleteOption[] => {
  if (!guide || !guide.sections) {
    return searchTerm.trim().length >= 2
      ? [{ type: 'fallback', label: `Search for "${searchTerm}"`, value: searchTerm }]
      : [];
  }

  // Build index from sections recursively
  const index = buildIndex(guide.sections);

  // Generate suggestions and filter out empty options
  return generateSuggestions(searchTerm, index, limit).filter(
    (option) => option.label && option.label.trim() !== '' && option.value && option.value.trim() !== ''
  );
};
